import { useEffect, useRef, useState } from 'react'
import { AlertCircle, AlertTriangle, CheckCircle2, HelpCircle, Info, type LucideIcon } from 'lucide-react'
import type { NormalizedDialog, DialogTone, DialogValue } from '../../stores/dialogStore'

const TONE: Record<DialogTone, { Icon: LucideIcon; medallion: string; primaryBtn: string }> = {
  default:     { Icon: HelpCircle,    medallion: 'bg-primary/12 text-primary',         primaryBtn: 'bg-primary text-primary-foreground' },
  destructive: { Icon: AlertTriangle, medallion: 'bg-destructive/12 text-destructive', primaryBtn: 'bg-destructive text-destructive-foreground' },
  warning:     { Icon: AlertCircle,   medallion: 'bg-amber-500/15 text-amber-500',     primaryBtn: 'bg-primary text-primary-foreground' },
  success:     { Icon: CheckCircle2,  medallion: 'bg-emerald-500/15 text-emerald-500', primaryBtn: 'bg-primary text-primary-foreground' },
  info:        { Icon: Info,          medallion: 'bg-sky-500/15 text-sky-500',         primaryBtn: 'bg-primary text-primary-foreground' },
}

function focusables(root: HTMLElement | null): HTMLElement[] {
  if (!root) return []
  return Array.from(
    root.querySelectorAll<HTMLElement>('button, [href], input, textarea, select, [tabindex]:not([tabindex="-1"])'),
  ).filter((el) => !el.hasAttribute('disabled'))
}

export default function DialogCard({ dialog: d, leaving, onResolve }: {
  dialog: NormalizedDialog
  leaving: boolean
  onResolve: (value: DialogValue) => void
}) {
  const [value, setValue] = useState(d.defaultValue)
  const [entered, setEntered] = useState(false)
  const cardRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const confirmRef = useRef<HTMLButtonElement>(null)
  const prevFocus = useRef<HTMLElement | null>(null)

  // Defensive: module code reaches dialog() through runtime-rescued import
  // paths that tsc can't type-check, so an off-enum tone string ('danger')
  // must degrade to the default look — not destructure-crash the whole
  // page into the error boundary (bit the GitLab card 2026-06-12).
  const tone = TONE[d.tone] ?? TONE.default
  const canConfirm = !d.isPrompt || !d.required || value.trim().length > 0

  const submit = () => {
    if (leaving || !canConfirm) return
    if (d.isPrompt) onResolve(value)
    else if (d.hasCancel) onResolve(true)
    else onResolve(undefined)
  }
  const dismiss = () => {
    if (leaving) return
    if (d.isPrompt) onResolve(null)
    else if (d.hasCancel) onResolve(false)
    else onResolve(undefined)
  }

  // Keep refs to the latest submit/dismiss so the stable keyboard listener
  // always calls current closures (with the latest `leaving`) without re-registering.
  const submitRef = useRef(submit)
  const dismissRef = useRef(dismiss)
  useEffect(() => { submitRef.current = submit; dismissRef.current = dismiss })

  // Enter animation: flip to "entered" next frame so the transition runs.
  useEffect(() => {
    const r = requestAnimationFrame(() => setEntered(true))
    return () => cancelAnimationFrame(r)
  }, [])

  // Capture the previously-focused element on mount; restore it on unmount.
  useEffect(() => {
    prevFocus.current = document.activeElement as HTMLElement | null
    return () => { prevFocus.current?.focus?.() }
  }, [])

  // Focus the default control shortly after mount.
  useEffect(() => {
    const id = window.setTimeout(() => {
      if (d.isPrompt) { inputRef.current?.focus(); inputRef.current?.select() }
      else confirmRef.current?.focus()
    }, 30)
    return () => window.clearTimeout(id)
  }, [d.isPrompt])

  // Esc cancels, Enter triggers primary, Tab is trapped. Stable listener that
  // reads the latest submit/dismiss via refs.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); dismissRef.current() }
      else if (e.key === 'Enter') {
        if (!d.isPrompt || document.activeElement === inputRef.current) { e.preventDefault(); submitRef.current() }
      } else if (e.key === 'Tab') {
        const els = focusables(cardRef.current)
        if (els.length === 0) return
        const first = els[0]; const last = els[els.length - 1]
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus() }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus() }
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [d.isPrompt])

  const show = entered && !leaving
  const { Icon } = tone

  return (
    <div
      className={`fixed inset-0 z-[1200] flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm transition-opacity duration-150 sm:items-center sm:p-6 ${show ? 'opacity-100' : 'opacity-0'}`}
      onClick={dismiss}
    >
      <div
        ref={cardRef}
        role={!d.isPrompt && (d.hasCancel || d.tone === 'destructive' || d.tone === 'warning') ? 'alertdialog' : 'dialog'}
        aria-modal="true"
        aria-labelledby={d.title ? 'dialog-title' : d.message ? 'dialog-message' : undefined}
        aria-describedby={d.title && d.message ? 'dialog-message' : undefined}
        aria-label={!d.title && !d.message ? 'Dialog' : undefined}
        onClick={(e) => e.stopPropagation()}
        className={`w-full bg-card shadow-2xl ring-1 ring-border transition duration-150 sm:w-[440px] sm:max-w-[calc(100vw-3rem)] rounded-t-2xl pb-[env(safe-area-inset-bottom)] sm:rounded-2xl sm:pb-0 ${show ? 'translate-y-0 scale-100 opacity-100' : 'translate-y-3 opacity-0 sm:translate-y-0 sm:scale-95'}`}
      >
        <div className="flex items-start gap-3 px-5 pt-5">
          <span className={`inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${tone.medallion}`}>
            <Icon size={18} />
          </span>
          <div className="min-w-0 flex-1 pt-0.5">
            {d.title && <h2 id="dialog-title" className="text-[15px] font-semibold text-foreground">{d.title}</h2>}
            {d.message && (
              <p
                id="dialog-message"
                className={d.title
                  ? 'mt-1 whitespace-pre-line text-[13px] leading-relaxed text-muted-foreground'
                  : 'whitespace-pre-line text-[14px] font-medium leading-relaxed text-foreground'}
              >
                {d.message}
              </p>
            )}
          </div>
        </div>

        {d.isPrompt && (
          <div className="px-5 pt-3">
            <input
              ref={inputRef}
              type={d.inputType}
              value={value}
              placeholder={d.placeholder}
              onChange={(e) => setValue(e.target.value)}
              className="w-full rounded-lg bg-muted px-3 py-2 text-sm text-foreground outline-none ring-1 ring-border focus:ring-2 focus:ring-primary/40"
            />
          </div>
        )}

        <div className="flex items-center justify-end gap-2 px-5 pb-5 pt-4">
          {d.hasCancel && (
            <button type="button" onClick={dismiss} className="rounded-full px-4 py-2 text-[13px] text-foreground transition hover:bg-muted/60">
              {d.cancelLabel}
            </button>
          )}
          <button
            ref={confirmRef}
            type="button"
            onClick={submit}
            disabled={!canConfirm}
            className={`rounded-full px-4 py-2 text-[13px] font-medium shadow-sm transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50 ${tone.primaryBtn}`}
          >
            {d.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
